import { SignupStageHandlerComponent } from "./SignupStageHandlerComponent.type";
import { Formik } from "formik";
import { renderFieldError } from "../../utils/form";
import { useCallback, useEffect, useState } from "react";
import { RegistrationFormContextShape } from "../../contexts/form";
import FormLayout from "../../layouts/form";
import { services } from "../../services";
import { uploadFile } from "../../utils/upload";
import { toFormikValidationSchema } from "zod-formik-adapter";
import { z } from "zod";
import { CreateLearnerPayload } from "@iibf/shared-resources/dto/create-learner";
import { getFileAsImage } from "../../utils/file";
import { resolvers } from "../../utils/pincode";
import { BatchList } from "./PersonaDetails/BatchList";
import BaseLayout from "../../layouts/base";
import { error } from "console";
import DataTable from "react-data-table-component";
import { useSearchParams } from "react-router-dom";
import { courseDraMap, CourseType } from "../../config";
import { getCourseIds } from "../../utils/getCourseIds";
import { draCourseIds } from "../../config";
import usePromise from "../../utils/hooks/usePromise";
import { useLocation } from "react-router-dom";
import { PAGE_PATHS } from "../page-paths";
import moment from "moment";
import {
  examStatesAndCities,
  examLanguages,
} from "../../utils/examCitiesDetails";

interface IBatch {
  id: number;
  batch_name: string;
  from_date: string;
  to_date: string;
  slot_start_time: string;
  slot_end_time: string;
  max_learners: number;
  userEnrollmentBatchArray_aggregate: {
    aggregate: {
      count: number;
    };
  };
  language: {
    display_name: string;
  };
  course: {
    id: number;
    full_name: string;
  };
  registration_end_date: string;
}

export const DUMMY_DRA_BATCHES: Record<"graduate" | "undergraduate", IBatch> = {
  graduate: {
    id: 1,
    batch_name: "DRA Graduate (50 hrs) - Staging Test Batch",
    from_date: new Date().toISOString(),
    to_date: new Date(Date.now() + 30 * 86400000).toISOString(),
    slot_start_time: "10:00 AM",
    slot_end_time: "05:00 PM",
    max_learners: 100,
    userEnrollmentBatchArray_aggregate: {
      aggregate: { count: 0 },
    },
    language: {
      display_name: "English",
    },
    course: {
      id: Number(process.env.REACT_APP_ST_DRA_GRADUATE_COURSE_ID || 8),
      full_name: "DRA-For Graduates",
    },
    registration_end_date: new Date(Date.now() + 15 * 86400000).toISOString(),
  },
  undergraduate: {
    id: 2,
    batch_name: "DRA Under Graduate (100 hrs) - Staging Test Batch",
    from_date: new Date().toISOString(),
    to_date: new Date(Date.now() + 30 * 86400000).toISOString(),
    slot_start_time: "10:00 AM",
    slot_end_time: "05:00 PM",
    max_learners: 100,
    userEnrollmentBatchArray_aggregate: {
      aggregate: { count: 0 },
    },
    language: {
      display_name: "English",
    },
    course: {
      id: Number(process.env.REACT_APP_ST_DRA_UNDER_GRADUATE_COURSE_ID || 9),
      full_name: "DRA-For Under Graduates",
    },
    registration_end_date: new Date(Date.now() + 15 * 86400000).toISOString(),
  },
};

export const PersonalDetailsPage: SignupStageHandlerComponent = ({
  stepNumber,
  stepTitle,
  formState,
  setFieldValue,
  clearForm,
}) => {
  // Bypassed batch selection screen directly to personalDetailForm while Hasura is down
  const [showBatchSelection, setShowBatchSelection] = useState(false);
  const [batchData, setBatchData] = useState<IBatch[]>([
    DUMMY_DRA_BATCHES.graduate,
    DUMMY_DRA_BATCHES.undergraduate,
  ]);
  const [selectedBatch, setSelectedBatch] = useState<IBatch>(
    DUMMY_DRA_BATCHES.graduate,
  );
  const [showPopup, setShowPopup] = useState(false);
  const [popupAcknowledged, setPopupAcknowledged] = useState(true);
  const [searchParams] = useSearchParams();
  const [partners, setPartners] = useState([]);
  const [error, setError] = useState("");
  const [selectedExamState, setSelectedExamState] = useState("");
  const [examCityOptions, setExamCityOptions] = useState<string[]>([]);
  const course = searchParams.get("course") as CourseType | null;
  const course_ids = course ? courseDraMap[course] : undefined;

  const location = useLocation();
  const isSelfOnboarding = location.pathname.includes(PAGE_PATHS.SIGNUP_SELF);
  async function getAvailableBatches() {
    let url = "/get-available-batches";
    let params: any = {};
    const courseIdsSpecificHostname = getCourseIds();

    if (course_ids) params.course_ids = course_ids;
    else if (courseIdsSpecificHostname.length > 0)
      params.course_ids = courseIdsSpecificHostname;

    try {
      const response = await services.requestSvc.get(url, { params });
      if (response.data && response.data.length > 0) {
        setBatchData(response.data);
        return response.data;
      }
      return [DUMMY_DRA_BATCHES.graduate, DUMMY_DRA_BATCHES.undergraduate];
    } catch (error) {
      console.warn("Error fetching batches from backend, using fallback dummy batches:", error);
      return [DUMMY_DRA_BATCHES.graduate, DUMMY_DRA_BATCHES.undergraduate];
    }
  }

  useEffect(() => {
    if (showBatchSelection) {
      getAvailableBatches();
    }
  }, [showBatchSelection]);

  // NOTE: Removed useEffect that synced selectedBatch → setFieldValue("batchId"/"courseId").
  // It caused an infinite re-render loop because setFieldValue changes on every render.
  // batchId/courseId are set directly in handleSubmit and in the radio/qualification onChange handlers.

  // const handleBatchSubmit = () => {
  //   setFieldValue("batchId", selectedBatch);
  // };

  const handleSubmit = useCallback(
    async (values: Partial<PersonalDetailsFormrResponseShape>) => {
      const effectiveBatch = selectedBatch || DUMMY_DRA_BATCHES.graduate;

      // Set all personal detail fields in form context
      setFieldValue("isPersonalDetailsFinalised", true);
      setFieldValue("name", values.name);
      setFieldValue("salutation", values.salutation);
      setFieldValue("email", values.email);
      setFieldValue("mobileNumber", values.mobileNumber);
      setFieldValue("gender", values.gender);
      setFieldValue("dateOfBirth", values.dateOfBirth);
      setFieldValue("category", values.category);
      setFieldValue("fatherName", values.fatherName);
      setFieldValue("isPhysicallyChallenged", values.isPhysicallyChallenged);
      setFieldValue("highestQualification", values.highestQualification);
      setFieldValue("addressLine1", values.addressLine1);
      setFieldValue("pincode", values.pincode);
      setFieldValue("identityProofType", values.identityProofType);
      setFieldValue("identityProofNumber", values.identityProofNumber);
      setFieldValue("batchId", effectiveBatch.id);
      setFieldValue("courseId", effectiveBatch.course.id);
      setFieldValue("partnerId", values.partnerId || "");
      setFieldValue("examCenterState", values.examCenterState || "");
      setFieldValue("examCenterCity", values.examCenterCity || "");
      setFieldValue("examLanguage", values.examLanguage || "");

      const latestBatchData = await getAvailableBatches();

      // Only perform strict batch validation if live batches were returned
      if (
        latestBatchData &&
        latestBatchData.length > 0 &&
        !latestBatchData.some((b: any) => b.id === 1 || b.id === 2)
      ) {
        const currentBatch = latestBatchData.find(
          (b: any) => b.id === effectiveBatch.id,
        );
        if (!currentBatch) {
          alert(
            "Selected batch not found or filled. Please choose a new batch again.",
          );
          setShowBatchSelection(true);
          return;
        }
      }

      try {
        // Upload all files and collect their IDs directly (not via stale formState)
        const idProofUpload = await uploadFile(values.identityProofFile!);
        const qualCertUpload = await uploadFile(values.highestQualificationCertificateFile!);
        const photoUpload = await uploadFile(values.candidatePhotoFile!);
        const signatureUpload = await uploadFile(values.candidateSignatureFile!);

        const identityProofFileId = Number(idProofUpload.id);
        const highestQualificationCertificateFileId = Number(qualCertUpload.id);
        const candidatePhotoFileId = Number(photoUpload.id);
        const candidateSignatureFileId = Number(signatureUpload.id);

        // Set file IDs in form context
        setFieldValue("identityProofFileId", identityProofFileId);
        setFieldValue("highestQualificationCertificateFileId", highestQualificationCertificateFileId);
        setFieldValue("candidatePhotoFileId", candidatePhotoFileId);
        setFieldValue("candidateSignatureFileId", candidateSignatureFileId);

        // Build a fresh validation payload directly (avoids stale formState closure)
        const validationPayload = {
          name: values.name,
          salutation: values.salutation,
          email: values.email,
          mobileNumber: values.mobileNumber,
          gender: values.gender,
          dateOfBirth: values.dateOfBirth,
          category: values.category,
          fatherName: values.fatherName,
          isPhysicallyChallenged: values.isPhysicallyChallenged,
          highestQualification: values.highestQualification,
          addressLine1: values.addressLine1,
          pincode: values.pincode,
          identityProofType: values.identityProofType,
          identityProofNumber: values.identityProofNumber,
          identityProofFileId,
          highestQualificationCertificateFileId,
          candidatePhotoFileId,
          candidateSignatureFileId,
          batchId: effectiveBatch.id,
          courseId: effectiveBatch.course.id,
          partnerId: values.partnerId || "",
          examCenterState: values.examCenterState || "",
          examCenterCity: values.examCenterCity || "",
          examLanguage: values.examLanguage || "",
        };

        // Validate payload
        await services.requestSvc.post(
          "/validate-learner-payload",
          validationPayload,
        );

        setFieldValue("personalDetailsErr", undefined);
        setFieldValue("isPersonalDetailsVerified", true);
      } catch (err: any) {
        setFieldValue("personalDetailsErr", err.message);
        setFieldValue("isPersonalDetailsVerified", false);
      }
    },
    [setFieldValue, selectedBatch],
  );
  const {
    result: fetchPartnersRes,
    error: fetchPartnersErr,
    loading: fetchingPartners,
  } = usePromise(
    {
      fn: async () => {
        return await services.requestSvc.get("/partners");
      },
      runOnMount: { args: [] },
      runOnRerender: { enabled: true, args: [] },
    },
    [],
  );

  useEffect(() => {
    if (fetchPartnersRes) {
      console.log("fetchPartnersRes.data", fetchPartnersRes);
      setPartners(fetchPartnersRes);
    }
    if (fetchPartnersErr) {
      setError("Error fetching partners.");
    }
  }, [fetchPartnersRes, fetchPartnersErr]);
  const stateCityMap: Record<string, string[]> = examStatesAndCities;
  useEffect(() => {
    if (selectedExamState) {
      setExamCityOptions(stateCityMap[selectedExamState] || []);
    } else {
      setExamCityOptions([]);
    }
  }, [selectedExamState]);
  const courseIdsSpecificHostname = getCourseIds();
  const isDRA = courseIdsSpecificHostname?.some((id) =>
    draCourseIds.includes(id),
  );
  console.log("batchData!@", batchData);
  const personalDetailForm = () => {
    return (
      <FormLayout
        stepNumber={stepNumber}
        stepTitle={stepTitle || ""}
        clearForm={clearForm}
      >
        <div className="mb-4 p-4 bg-blue-50 border border-blue-200 rounded-lg">
          <div className="font-semibold text-gray-800 mb-2">
            Selected DRA Course & Batch (Staging Bypass):
          </div>
          <div className="flex flex-col sm:flex-row gap-4">
            <label className="flex items-center gap-2 cursor-pointer bg-white px-3 py-2 rounded border border-gray-200 shadow-sm">
              <input
                type="radio"
                name="draCourseChoice"
                checked={
                  selectedBatch?.course.id ===
                  DUMMY_DRA_BATCHES.graduate.course.id
                }
                onChange={() => {
                  setSelectedBatch(DUMMY_DRA_BATCHES.graduate);
                  setFieldValue("batchId", DUMMY_DRA_BATCHES.graduate.id);
                  setFieldValue("courseId", DUMMY_DRA_BATCHES.graduate.course.id);
                }}
              />
              <span className="text-sm font-medium text-gray-800">
                🎓 DRA Graduate (50 hrs) [Course ID:{" "}
                {DUMMY_DRA_BATCHES.graduate.course.id}]
              </span>
            </label>
            <label className="flex items-center gap-2 cursor-pointer bg-white px-3 py-2 rounded border border-gray-200 shadow-sm">
              <input
                type="radio"
                name="draCourseChoice"
                checked={
                  selectedBatch?.course.id ===
                  DUMMY_DRA_BATCHES.undergraduate.course.id
                }
                onChange={() => {
                  setSelectedBatch(DUMMY_DRA_BATCHES.undergraduate);
                  setFieldValue("batchId", DUMMY_DRA_BATCHES.undergraduate.id);
                  setFieldValue(
                    "courseId",
                    DUMMY_DRA_BATCHES.undergraduate.course.id,
                  );
                }}
              />
              <span className="text-sm font-medium text-gray-800">
                📚 DRA Under Graduate (100 hrs) [Course ID:{" "}
                {DUMMY_DRA_BATCHES.undergraduate.course.id}]
              </span>
            </label>
          </div>
          <div className="mt-2 text-xs text-blue-700">
            Active Batch:{" "}
            <span className="font-semibold">{selectedBatch?.batch_name}</span>
          </div>
        </div>
        <div className="italic mb-4">
          <span className="text-lg font-semibold text-orange-500">NOTICE</span>
          <p>
            Please be ready with the following documents before the registration
            process.
            <ul className="list-disc ml-4">
              <li>
                Highest Qualification Certificate document: png/jpg format, less
                than 5MB
              </li>
              <li>Identity Proof Document: png/jpg format, less than 5MB</li>
              <li>
                Candidate Passport Size Photo: png/jpg format, less than 5MB
              </li>
              <li>Candidate Signature: png/jpg format, less than 5MB</li>
              <li>
                Submit the highest qualification certificate document according
                to your selected batch category.
              </li>
            </ul>
          </p>
        </div>
        <Formik<Partial<PersonalDetailsFormrResponseShape>>
          initialValues={{
            name: formState.name,
            salutation: formState.salutation,
            mobileNumber: formState.mobileNumber,
            email: formState.email,
            gender: formState.gender,
            dateOfBirth: formState.dateOfBirth,
            category: formState.category,
            fatherName: formState.fatherName,
            isPhysicallyChallenged: formState.isPhysicallyChallenged,
            highestQualification: formState.highestQualification,
            identityProofType: formState.identityProofType,
            identityProofNumber: formState.identityProofNumber,
            addressLine1: formState.addressLine1,
            pincode: formState.pincode,
            batchId: formState.batchId,
            courseId: formState.courseId,
            partnerId: formState.partnerId,
            examCenterState: formState.examCenterState,
            examCenterCity: formState.examCenterCity,
            examLanguage: formState.examLanguage,
          }}
          onSubmit={handleSubmit}
          validationSchema={toFormikValidationSchema(
            PersonalDetailsFormResponseSchema,
          )}
        >
          {({
            values,
            errors,
            handleChange,
            handleBlur,
            handleSubmit,
            setFieldValue,
            isSubmitting,
          }) => {
            return (
              <form className="mt-3" onSubmit={handleSubmit}>
                {formState.personalDetailsErr ? (
                  <div className="text-red-500 mb-4 font-semibold">
                    {formState.personalDetailsErr}
                  </div>
                ) : (
                  ""
                )}
                <div className="flex flex-col gap-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-y-2 gap-x-2 items-start">
                    <div className="col-span-2 xl:col-span-3 font-semibold mb-4 text-lg">
                      Personal Details
                    </div>
                    <div className="flex flex-col w-full gap-y-1">
                      <span className="font-semibold">Salutation</span>
                      <select
                        className="input-primary"
                        name="salutation"
                        onChange={handleChange}
                        defaultValue={formState.salutation}
                      >
                        <option value="">Choose salutation</option>
                        <option value="mr">Mr</option>
                        <option value="mrs">Mrs</option>
                        <option value="miss">Miss</option>
                      </select>
                      {renderFieldError(errors, "salutation")}
                    </div>
                    <div className="flex flex-col w-full gap-y-1">
                      <span className="font-semibold">Name</span>
                      <input
                        type="text"
                        name="name"
                        onChange={(e) =>
                          setFieldValue("name", e.target.value.toUpperCase())
                        }
                        onBlur={(e) =>
                          setFieldValue("name", e.target.value.toUpperCase())
                        }
                        value={values.name}
                        defaultValue={values.name}
                        placeholder="Name"
                        className="input-primary"
                      />
                      <span className="text-blue text-xs font-semibold">
                        Full name MUST match uploaded identity proof.
                      </span>
                      {renderFieldError(errors, "name")}
                    </div>
                    <div className="flex flex-col w-full gap-y-1">
                      <span className="font-semibold">Mobile Number</span>
                      <input
                        type="text"
                        name="mobileNumber"
                        onChange={handleChange}
                        onBlur={handleBlur}
                        defaultValue={values.mobileNumber}
                        placeholder="Mobile Number"
                        className="input-primary"
                      />
                      {renderFieldError(errors, "mobileNumber")}
                    </div>
                    <div className="flex flex-col w-full gap-y-1">
                      <span className="font-semibold">Gender</span>
                      <select
                        className="input-primary"
                        name="gender"
                        onChange={handleChange}
                        defaultValue={values.gender}
                      >
                        <option>Choose gender</option>
                        <option value="male">Male</option>
                        <option value="female">Female</option>
                        <option value="other">Other</option>
                      </select>
                      {renderFieldError(errors, "gender")}
                    </div>
                    <div className="flex flex-col w-full gap-y-1">
                      <span className="font-semibold">Email</span>
                      <input
                        type="text"
                        name="email"
                        onChange={handleChange}
                        onBlur={handleBlur}
                        defaultValue={values.email}
                        placeholder="Email"
                        className="input-primary"
                      />
                      {renderFieldError(errors, "email")}
                    </div>

                    <div className="flex flex-col w-full gap-y-1">
                      <span className="font-semibold">
                        Date of Birth(Minimum Age: 18 Years)
                      </span>
                      <input
                        type="date"
                        name="dateOfBirth"
                        onChange={(e) => {
                          try {
                            return setFieldValue(
                              "dateOfBirth",
                              new Date(e.target.value).toISOString(),
                            );
                          } catch (err) {}
                        }}
                        onBlur={(e) => {
                          try {
                            return setFieldValue(
                              "dateOfBirth",
                              new Date(e.target.value).toISOString(),
                            );
                          } catch (err) {}
                        }}
                        defaultValue={values.dateOfBirth?.split("T")[0]}
                        placeholder="Date of Birth"
                        className="input-primary"
                      />
                      {renderFieldError(errors, "dateOfBirth")}
                    </div>
                    <div className="flex flex-col w-full gap-y-1">
                      <span className="font-semibold">Category</span>
                      <select
                        name="category"
                        onChange={handleChange}
                        onBlur={handleBlur}
                        defaultValue={values.category}
                        className="input-primary"
                      >
                        <option value="">Choose a category</option>
                        <option value="general">General</option>
                        <option value="sc">SC</option>
                        <option value="st">ST</option>
                        <option value="obc">OBC</option>
                      </select>
                      {renderFieldError(errors, "category")}
                    </div>
                    <div className="flex flex-col w-full gap-y-1">
                      <span className="font-semibold">Father's Name</span>
                      <input
                        type="text"
                        name="fatherName"
                        onChange={(e) =>
                          setFieldValue(
                            "fatherName",
                            e.target.value.toUpperCase(),
                          )
                        }
                        onBlur={(e) =>
                          setFieldValue(
                            "fatherName",
                            e.target.value.toUpperCase(),
                          )
                        }
                        value={values.fatherName}
                        defaultValue={values.fatherName}
                        placeholder="Father's Name"
                        className="input-primary"
                      />
                      {renderFieldError(errors, "fatherName")}
                    </div>
                    <div className="flex flex-col w-full gap-y-1">
                      <span className="font-semibold">
                        Is Physically Challenged?
                      </span>
                      <select
                        name="isPhysicallyChallenged"
                        onChange={(e) => {
                          return setFieldValue(
                            "isPhysicallyChallenged",
                            e.target.value === "true" ? true : false,
                          );
                        }}
                        defaultValue={
                          typeof values.isPhysicallyChallenged === "boolean"
                            ? values.isPhysicallyChallenged
                              ? "true"
                              : "false"
                            : ""
                        }
                        className="input-primary"
                      >
                        <option value="">Choose an option</option>
                        <option value="true">Yes</option>
                        <option value="false">No</option>
                      </select>
                      {renderFieldError(errors, "isPhysicallyChallenged")}
                    </div>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-y-2 gap-x-2 items-start">
                    <div className="col-span-2 xl:col-span-3 font-semibold mb-4 text-lg">
                      Address Details
                    </div>
                    <div className="flex flex-col w-full gap-y-1 col-span-2">
                      <span className="font-semibold">Complete Address</span>
                      <input
                        type="text"
                        name="addressLine1"
                        onChange={(e) =>
                          setFieldValue(
                            "addressLine1",
                            e.target.value.toUpperCase(),
                          )
                        }
                        onBlur={(e) =>
                          setFieldValue(
                            "addressLine1",
                            e.target.value.toUpperCase(),
                          )
                        }
                        value={values.addressLine1}
                        defaultValue={values.addressLine1}
                        placeholder="Complete Address"
                        className="input-primary"
                      />
                      {renderFieldError(errors, "addressLine1")}
                    </div>
                    <div className="flex flex-col w-full gap-y-1">
                      <span className="font-semibold">Pincode</span>
                      <input
                        type="text"
                        name="pincode"
                        onChange={handleChange}
                        onBlur={handleBlur}
                        defaultValue={values.pincode}
                        placeholder="Pincode"
                        className="input-primary"
                      />
                      {renderFieldError(errors, "pincode")}
                    </div>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-y-2 gap-x-2 items-start">
                    <div className="col-span-2 xl:col-span-3 font-semibold mb-4 text-lg">
                      Education Details
                    </div>
                    <div className="flex flex-col w-full gap-y-1">
                      <span className="font-semibold">
                        Highest Qualification
                      </span>
                      <select
                        name="highestQualification"
                        onChange={(e) => {
                          handleChange(e);
                          const qual = e.target.value;
                          if (qual === "q_10" || qual === "q_12") {
                            setSelectedBatch(DUMMY_DRA_BATCHES.undergraduate);
                            setFieldValue("batchId", DUMMY_DRA_BATCHES.undergraduate.id);
                            setFieldValue(
                              "courseId",
                              DUMMY_DRA_BATCHES.undergraduate.course.id,
                            );
                          } else if (
                            qual === "q_graduate" ||
                            qual === "q_post_graduate"
                          ) {
                            setSelectedBatch(DUMMY_DRA_BATCHES.graduate);
                            setFieldValue("batchId", DUMMY_DRA_BATCHES.graduate.id);
                            setFieldValue(
                              "courseId",
                              DUMMY_DRA_BATCHES.graduate.course.id,
                            );
                          }
                        }}
                        onBlur={handleBlur}
                        defaultValue={values.highestQualification}
                        className="input-primary"
                      >
                        <option value="">Choose a qualification</option>
                        {[
                          { value: "q_10", label: "10th Standard" },
                          { value: "q_12", label: "12th Standard" },
                          { value: "q_graduate", label: "Graduate" },
                          { value: "q_post_graduate", label: "Post Graduate" },
                        ].map((o) => (
                          <option value={o.value}>{o.label}</option>
                        ))}
                      </select>
                      {renderFieldError(errors, "highestQualification")}
                    </div>

                    <div className="flex flex-col w-full gap-y-1">
                      <span className="font-semibold">
                        Highest Qualification Certificate
                      </span>

                      <input
                        type="file"
                        name="highestQualificationCertificateFile"
                        onChange={(e) => {
                          setFieldValue(
                            "highestQualificationCertificateFile",
                            e.target.files?.[0],
                          );
                        }}
                        placeholder="Choose Highest Qualification File"
                        className="input-primary"
                        accept=".png, .jpg, .jpeg"
                      />
                      <span className="text-blue text-xs font-semibold">
                        png/jpg format, less than 5MB
                      </span>

                      {renderFieldError(
                        errors,
                        "highestQualificationCertificateFile",
                      )}
                    </div>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-y-2 gap-x-2 items-start">
                    <div className="col-span-2 xl:col-span-3 font-semibold mb-4 text-lg">
                      Identity Documents
                    </div>
                    <div className="flex flex-col w-full gap-y-1">
                      <span className="font-semibold">Identity Proof Type</span>
                      <select
                        name="identityProofType"
                        onChange={handleChange}
                        onBlur={handleBlur}
                        defaultValue={values.identityProofType}
                        className="input-primary"
                      >
                        <option value="">Choose a proof type</option>
                        {[
                          {
                            value: "driving_license",
                            label: "Driving License",
                          },
                          { value: "employers_card", label: "Employer's Card" },
                          { value: "pan", label: "PAN Card" },
                          { value: "passport", label: "Passport" },
                        ].map((o) => (
                          <option value={o.value}>{o.label}</option>
                        ))}
                      </select>
                      {renderFieldError(errors, "identityProofType")}
                    </div>
                    <div className="flex flex-col w-full gap-y-1">
                      <span className="font-semibold">
                        Identity Proof Number
                      </span>
                      <input
                        type="text"
                        name="identityProofNumber"
                        onChange={(e) =>
                          setFieldValue(
                            "identityProofNumber",
                            e.target.value.toUpperCase(),
                          )
                        }
                        onBlur={(e) =>
                          setFieldValue(
                            "identityProofNumber",
                            e.target.value.toUpperCase(),
                          )
                        }
                        value={values.identityProofNumber}
                        defaultValue={values.identityProofNumber}
                        placeholder="Identity Proof Number"
                        className="input-primary"
                      />
                      {renderFieldError(errors, "identityProofNumber")}
                    </div>
                    <div className="flex flex-col w-full gap-y-1">
                      <span className="font-semibold">Identity Proof File</span>
                      <input
                        type="file"
                        name="identityProofFile"
                        onChange={(e) => {
                          setFieldValue(
                            "identityProofFile",
                            e.target.files?.[0],
                          );
                        }}
                        placeholder="Choose Identity Proof File"
                        className="input-primary"
                        accept=".png, .jpg, .jpeg"
                      />
                      <span className="text-blue text-xs font-semibold">
                        png/jpg format, less than 5MB
                      </span>

                      {renderFieldError(errors, "identityProofFile")}
                    </div>
                    <div className="flex flex-col w-full gap-y-1">
                      <span className="font-semibold">Passport Size Photo</span>
                      <input
                        type="file"
                        name="candidatePhotoFile"
                        onChange={(e) => {
                          setFieldValue(
                            "candidatePhotoFile",
                            e.target.files?.[0],
                          );
                        }}
                        placeholder="candidatePhotoFile"
                        className="input-primary"
                        accept=".png, .jpg, .jpeg"
                      />
                      <span className="text-blue text-xs font-semibold">
                        png/jpg format, less than 5MB
                      </span>
                      <span className="text-blue text-xs font-semibold">
                        Please upload your current photo only.
                      </span>

                      {renderFieldError(errors, "candidatePhotoFile")}
                    </div>
                    <div className="flex flex-col w-full gap-y-1">
                      <span className="font-semibold">Signature</span>
                      <input
                        type="file"
                        name="candidateSignatureFile"
                        onChange={(e) => {
                          setFieldValue(
                            "candidateSignatureFile",
                            e.target.files?.[0],
                          );
                        }}
                        placeholder="Choose candidate signature file"
                        className="input-primary"
                        accept=".png, .jpg, .jpeg"
                      />
                      <span className="text-blue text-xs font-semibold">
                        png/jpg format, less than 5MB
                      </span>

                      {renderFieldError(errors, "candidateSignatureFile")}
                    </div>
                    {/* <div className="flex flex-col w-full gap-y-1">
                      <span className="font-semibold">
                        Choose Partner(If any)
                      </span>
                      <select
                        name="partnerId"
                        onChange={(e) =>
                          setFieldValue("partnerId", e.target.value)
                        }
                        defaultValue={values.partnerId}
                        className="input-primary"
                      >
                        <option value="">Choose Partner</option>
                        {error ? (
                          <option>{error}</option>
                        ) : (
                          partners?.map((partner: any) => (
                            <option key={partner.id} value={partner.id}>
                              {partner.name}
                            </option>
                          ))
                        )}
                      </select>
                      {renderFieldError(errors, "partnerId")}
                    </div> */}
                  </div>
                  {isDRA && (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-y-2 gap-x-2 items-start">
                      <div className="col-span-2 xl:col-span-3 font-semibold mb-4 text-lg">
                        Exam Center Related Details
                      </div>
                      <div className="flex flex-col w-full gap-y-1">
                        <span className="font-semibold">Exam Center State</span>
                        <select
                          name="examCenterState"
                          onChange={(e) => {
                            const state = e.target.value;
                            setSelectedExamState(state);
                            setFieldValue("examCenterState", state);
                          }}
                          value={values.examCenterState}
                          defaultValue={values.examCenterState}
                          className="input-primary"
                          required
                        >
                          <option value="">Choose a state</option>
                          {Object.keys(stateCityMap).map((state) => (
                            <option key={state} value={state}>
                              {state}
                            </option>
                          ))}
                        </select>
                        {renderFieldError(errors, "examCenterState")}
                      </div>

                      <div className="flex flex-col w-full gap-y-1">
                        <span className="font-semibold">Exam Center City</span>
                        <select
                          name="examCenterCity"
                          onChange={(e) => {
                            const city = e.target.value;
                            setFieldValue("examCenterCity", city);
                          }}
                          value={values.examCenterCity}
                          defaultValue={values.examCenterCity}
                          className="input-primary"
                          disabled={!examCityOptions.length}
                          required
                        >
                          <option value="">Choose a city</option>
                          {examCityOptions.map((city) => (
                            <option key={city} value={city}>
                              {city}
                            </option>
                          ))}
                        </select>
                        {renderFieldError(errors, "examCenterCity")}
                      </div>

                      <div className="flex flex-col w-full gap-y-1">
                        <span className="font-semibold">Exam Language</span>
                        <select
                          name="examLanguage"
                          onChange={(e) => {
                            setFieldValue("examLanguage", e.target.value);
                          }}
                          value={values.examLanguage}
                          defaultValue={values.examLanguage}
                          className="input-primary"
                          required
                        >
                          <option value="">Choose exam language</option>
                          {examLanguages.map((lang) => (
                            <option key={lang} value={lang}>
                              {lang}
                            </option>
                          ))}
                        </select>
                        {renderFieldError(errors, "examLanguage")}
                      </div>
                    </div>
                  )}
                </div>
                <div className="flex flex-col mt-4">
                  <span>
                    <input
                      type="checkbox"
                      name="acceptedTC"
                      onChange={(e) =>
                        setFieldValue("acceptedTC", e.target.checked)
                      }
                      onBlur={(e) =>
                        setFieldValue("acceptedTC", e.target.checked)
                      }
                      checked={values.acceptedTC}
                      className="mr-2"
                    />
                    I confirm that the candidate is being enrolled for the
                    above-mentioned examination. I have ensured that all Rules,
                    Regulations, and instructions governing the examination of
                    the institute have been read and understood. The candidate
                    agrees to abide by all the said Rules, Regulations, and
                    instructions of the institute. It is also confirmed that the
                    candidate has not been debarred or disqualified from
                    appearing in the institute's examinations at the time of
                    this application. Additionally, it is agreed that any legal
                    proceedings initiated by the candidate against the institute
                    shall only be instituted in the Courts at New Delhi, and not
                    in any other Court.
                  </span>
                  {renderFieldError(errors, "acceptedTC")}
                </div>
                <div className="flex flex-col mt-4">
                  <span>
                    <input
                      type="checkbox"
                      name="agreedTC"
                      onChange={(e) =>
                        setFieldValue("agreedTC", e.target.checked)
                      }
                      onBlur={(e) =>
                        setFieldValue("agreedTC", e.target.checked)
                      }
                      checked={values.agreedTC}
                      className="mr-2"
                    />
                    I confirm that all the details which have been shared and
                    all the documents which have been uploaded are correct and
                    won’t be changed in future. I also understand that the fees
                    paid for this course is non refundable and shall not be
                    refunded in any case nor will the batch date be changed.
                  </span>
                  {renderFieldError(errors, "agreedTC")}
                </div>

                {isDRA && (
                  <div className="flex flex-col mt-4">
                    <span>
                      <input
                        type="checkbox"
                        name="agreedExamTC"
                        onChange={(e) =>
                          setFieldValue("agreedExamTC", e.target.checked)
                        }
                        onBlur={(e) =>
                          setFieldValue("agreedExamTC", e.target.checked)
                        }
                        required
                        className="mr-2"
                      />
                      I confirm that exam center details once submitted cannot
                      be changed under any circumstances.
                    </span>
                    {renderFieldError(errors, "agreedExamTC")}
                  </div>
                )}

                <input
                  type="submit"
                  value={isSubmitting ? "Please wait..." : "Continue"}
                  disabled={isSubmitting}
                  className="btn-primary mt-4 w-full"
                  // onClick={() => {
                  //   console.log("errors", errors);
                  //   setShowBatchSelection(true);
                  // }}
                />
              </form>
            );
          }}
        </Formik>
      </FormLayout>
    );
  };

  const batchSelectionForm = () => {
    const columns = [
      {
        name: "",
        cell: (row: IBatch) => (
          <input
            type="radio"
            checked={selectedBatch === row}
            onChange={() => {
              setSelectedBatch(row);

              if (isDRA) {
                setPopupAcknowledged(false);
                setShowPopup(true);
              } else {
                setPopupAcknowledged(true);
              }
            }}
          />
        ),
        width: "40px",
        ignoreRowClick: true,
        allowOverflow: true,
        button: true,
      },
      {
        name: "Batch Name",
        selector: (row: IBatch) => `${row.batch_name}`,
        sortable: true,
        wrap: true,
      },
      {
        name: "Language",
        selector: (row: IBatch) => `${row.language.display_name}`,
        sortable: true,
      },
      {
        name: (
          <div style={{ textWrap: "wrap", textAlign: "center" }}>
            {"Registration End Date"}
          </div>
        ),
        wrap: true,
        selector: (row: IBatch) =>
          moment(row.registration_end_date).format("ddd, DD-MMM-YYYY"),
      },
      {
        name: "Duration",
        selector: (row: IBatch) =>
          `${moment(row.from_date).format("DD-MMM-YYYY")} - ${moment(
            row.to_date,
          ).format("DD-MMM-YYYY")}`,
        sortable: true,
        wrap: true,
      },
      {
        name: "Timings",
        selector: (row: IBatch) =>
          `${row.slot_start_time} - ${row.slot_end_time}`,
      },
      {
        name: "Seats Available",
        selector: (row: IBatch) =>
          `${
            row.max_learners -
            row.userEnrollmentBatchArray_aggregate.aggregate.count
          }`,
      },
    ];
    return (
      <BaseLayout>
        <div className="flex gap-10">
          <div className="col-span-2 xl:col-span-3 font-semibold mb-4 text-lg">
            Select Batch
          </div>
          <div className="col-span-2 xl:col-span-3 font-semibold mb-4 text-lg">
            Total Live Batches:
            <span className="ml-1">{batchData?.length}</span>
          </div>
        </div>

        {/* <div className="italic mb-4">
          <span className="text-lg font-semibold text-orange-500">NOTICE</span>
          <p>
            <ul className="list-disc ml-4">
              <li>
                Fee submitted towards the selected batch and course is
                non-refundable. You are requested to cross-check the course and
                batch you are applying to before proceeding with the payment.
              </li>
              <li>
                Commencement of batches may be delayed beyond the set date in
                exceptions like -
                <ul>
                  <li>
                    (i) prescribed slots are not filled until last date of
                    registration
                  </li>
                  <li>
                    (ii) IIBF authorization for the batch is pending. You shall
                    receive due information in such cases.
                  </li>
                </ul>
              </li>
            </ul>
          </p>
        </div> */}
        <DataTable columns={columns} data={batchData} />
        <input
          type="batch-submit"
          value={
            selectedBatch
              ? popupAcknowledged
                ? "Continue"
                : "Please read the notice to continue"
              : "Please Select a Batch to Continue"
          }
          disabled={!selectedBatch || !popupAcknowledged}
          className="btn-primary mt-4 w-full text-center"
          onClick={() => {
            setFieldValue("batchId", selectedBatch?.id);
            setFieldValue("courseId", selectedBatch?.course.id);
            setShowBatchSelection(false);
          }}
        />
        {showPopup && (
          <div className="fixed inset-0 z-50 bg-black bg-opacity-50 flex justify-center items-center">
            <div className="bg-white p-6 rounded-lg max-w-xl shadow-lg">
              <h2 className="text-lg font-bold text-red-600 mb-4">
                ⚠️ Please Read Before Proceeding
              </h2>
              <div className="text-sm mb-4">
                <ul className="list-disc ml-5 space-y-1">
                  <li>
                    <span className="text-red-600 font-bold">
                      Fees once paid are non-refundable. Please double-check your
                      course and batch details (language, duration, timings)
                      before making payment.
                    </span>
                  </li>
                  <li>
                    100% attendance is mandatory as per IIBF guidelines.
                    Shortage in attendance will lead to disqualification from
                    training and exam.
                  </li>
                  <li>
                    Batch start dates may be delayed if:
                    <ul className="list-disc ml-5">
                      <li>Minimum registrations are not met</li>
                      <li>IIBF approval is pending</li>
                    </ul>
                    You will be informed in such cases.
                  </li>
                  <li>
                    Please note that exam center details once submitted cannot
                    be changed under any circumstances.
                  </li>
                  <li>
                    If you are selecting the Undergraduate batch, please submit
                    only 10th or 12th grade documents. If you are selecting the
                    Graduate batch, please submit documents related to
                    Graduation or Post-Graduation only.
                  </li>
                </ul>
                <p className="mt-2">
                  📄 For full details,&nbsp;
                  <a
                    href="https://iibf-uploads-prod.s3.ap-south-1.amazonaws.com/dra-prospectus-english.pdf"
                    download
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-sky-600  underline hover:text-blue-800"
                  >
                    download the prospectus here
                  </a>
                  .
                </p>

                <hr className="my-3" />

                <ul className="list-disc ml-5 text-gray-700">
                  <li>
                    <span className="text-red-600 font-bold">
                      फीस जमा होने के बाद वापस नहीं की जाएगी। कृपया कोर्स और बैच
                      की जानकारी (भाषा, अवधि, समय) ध्यान से जांच लें।
                    </span>
                  </li>
                  <li>
                    IIBF के अनुसार 100% उपस्थिति अनिवार्य है। उपस्थिति पूरी न
                    होने पर प्रशिक्षण और परीक्षा से अयोग्य घोषित कर दिया जाएगा।
                  </li>
                  <li>
                    बैच की शुरुआत में देरी हो सकती है यदि:
                    <ul className="list-disc ml-5">
                      <li>न्यूनतम पंजीकरण नहीं हुआ हो</li>
                      <li>IIBF से स्वीकृति लंबित हो</li>
                    </ul>
                    ऐसी स्थिति में आपको जानकारी दी जाएगी।
                  </li>
                  <li>
                    यदि आप अंडरग्रेजुएट बैच चुन रहे हैं, तो केवल 10वीं या 12वीं
                    के दस्तावेज़ जमा करें। यदि आप ग्रेजुएट बैच चुन रहे हैं, तो
                    केवल स्नातक या स्नातकोत्तर संबंधित दस्तावेज़ जमा करें।
                  </li>
                </ul>
                <p className="mt-2">
                  📄 सभी विवरणों के लिए,&nbsp;
                  <a
                    href="https://iibf-uploads-prod.s3.ap-south-1.amazonaws.com/prospectus-dra-training-hindi.pdf"
                    download
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-sky-600 underline hover:text-blue-800"
                  >
                    प्रॉस्पेक्टस डाउनलोड करें
                  </a>
                  .
                </p>
              </div>
              <div className="text-right">
                <button
                  onClick={() => {
                    setShowPopup(false);
                    setPopupAcknowledged(true);
                  }}
                  className="btn-primary"
                >
                  I have read and agreed on disclaimer (मैं सहमत हूं)
                </button>
              </div>
            </div>
          </div>
        )}
      </BaseLayout>
    );
  };
  const errorPage = () => {
    return (
      <div className="text-red-500">
        Dear Candidate, please note that enrollment for April batches - Debt
        Recovery Agent (DRA), is temporarily paused at CSC Academy. We will
        resume soon and this page will be updated. Thank you for your patience.
        <br />
        प्रिय उम्मीदवार, कृपया ध्यान दें कि CSC Academy में DRA अप्रैल बैच के
        लिए नामांकन अभी रुका हुआ है। हम जल्द ही दोबारा शुरुआत करेंगे और यह पेज
        अपडेट कर दिया जाएगा। धैर्य के लिए धन्यवाद।
      </div>
    );
  };

  return showBatchSelection ? batchSelectionForm() : personalDetailForm();
};

const uploadFileAndSetIdAsValue = async (
  file: File,
  valueSetter: RegistrationFormContextShape["setFieldValue"],
  key: keyof RegistrationFormContextShape["formState"],
) => {
  const fileUploadResponse = await uploadFile(file);
  valueSetter(key, Number(fileUploadResponse.id));
};

// File checks

type ZodRefinerFactory<T> = [(arg: T) => Promise<boolean> | boolean, string];

const fileTypeValidator = (): ZodRefinerFactory<File> => [
  (e: File) => ["image/png", "image/jpeg"].includes(e.type),
  "File should be .jpg, .jpeg or .png, .jpg, .jpeg",
];
const maxFileSizeValidator = (maxFileSize: number): ZodRefinerFactory<File> => [
  (e: File) => e.size <= maxFileSize,
  "File should be less than or equal to " +
    Math.floor(maxFileSize / 1024) +
    "KB",
];
const minFileSizeValidator = (minFileSize: number): ZodRefinerFactory<File> => [
  (e: File) => e.size >= minFileSize,
  "File should be more than or equal to " +
    Math.floor(minFileSize / 1024) +
    "KB",
];

const PersonalDetailsFormResponseSchema = z
  .object({})
  .merge(
    CreateLearnerPayload.omit({
      highestQualificationCertificateFileId: true,
      identityProofFileId: true,
      candidatePhotoFileId: true,
      candidateSignatureFileId: true,
    }),
  )
  .merge(
    z.object({
      pincode: CreateLearnerPayload.shape.pincode.refine(
        (p) => resolvers.IND(p).then((e) => !!e),
        "Pincode is invalid",
      ),

      identityProofFile: z
        .instanceof(File, { message: "Required" })
        .refine(...fileTypeValidator())
        .refine(...minFileSizeValidator(102400))
        .refine(...maxFileSizeValidator(5242880)),
      highestQualificationCertificateFile: z
        .instanceof(File, { message: "Required" })
        .refine(...fileTypeValidator())
        .refine(...minFileSizeValidator(102400))
        .refine(...maxFileSizeValidator(5242880)),
      candidatePhotoFile: z
        .instanceof(File, { message: "Required" })
        .refine(...fileTypeValidator())
        .refine(...minFileSizeValidator(102400))
        .refine(...maxFileSizeValidator(5242880)),
      candidateSignatureFile: z
        .instanceof(File, { message: "Required" })
        .refine(...fileTypeValidator())
        .refine(...minFileSizeValidator(102400))
        .refine(...maxFileSizeValidator(5242880)),

      acceptedTC: z
        .boolean({ message: "You must accept the acknowledgement" })
        .refine((e) => e, "You must accept the acknowledgement"),

      batchId: z.number().nullable(),
      courseId: z.number().nullable(),
      agreedTC: z
        .boolean({ message: "You must accept the agreement" })
        .refine((e) => e, "You must accept the agreement"),
      agreedExamTC: z
        .boolean({ message: "You must accept the agreement" })
        .refine((e) => e, "You must accept the agreement")
        .optional(),
    }),
  );

type PersonalDetailsFormrResponseShape = z.infer<
  typeof PersonalDetailsFormResponseSchema
>;
